/* eslint-disable array-bracket-newline */
/**
 * Implements `NumberDisplay` component.
 *
 * This component is designed to provide a flexible way to display numbers with various formatting options.
 * Features include:
 * 1. Rounding numbers to a specified precision.
 * 2. Displaying numbers with optional prefix (startSymbol) and suffix (endSymbol).
 * 3. Customizable display for zero values.
 * 4. Ability to render numbers in subscript format.
 * 5. Option to return the display as a plain string or wrap it in a Text component with additional properties.
 */
import Text, { ITextProps } from '../../atoms/Text';
import {
  BOLD,
  SUBSCRIPT,
  formatToRawNumber,
  roundToPrecision,
} from '../../helpers/truncate';

import './styles.scss';

export interface INumberDisplayProps {
  value?: number; // The number to be displayed
  round?: number; // The precision to which the number should be rounded
  startSymbol?: string; // A symbol or string to prefix the displayed number
  endSymbol?: string; // A symbol or string to suffix the displayed number
  zeroValueDisplay?: string | number; // What to display if the value is zero or undefined
  textProps?: Omit<ITextProps, 'children'>; // Props to pass down to the Text component, excluding 'children'
  string?: boolean; // If true, the component will return a string instead of JSX
}

const NumberDisplay = ({
  value,
  round,
  startSymbol = '',
  endSymbol = '',
  zeroValueDisplay,
  textProps,
  string,
}: INumberDisplayProps) => {
  // Check if the value is zero or undefined and zeroValueDisplay defined.
  // If so, we'll display only zeroValueDisplay.
  const isZeroValue = zeroValueDisplay !== undefined && !value;

  if (isZeroValue) {
    if (string || !textProps) {
      return <>{zeroValueDisplay}</>;
    }
    return <Text {...textProps}>{zeroValueDisplay}</Text>;
  }

  // Round the value to the specified precision
  const parsedValue = roundToPrecision(value, false, round);

  // Check if the parsedValue contains a SUBSCRIPT. If so, we'll display it differently.
  if (parsedValue.includes(SUBSCRIPT)) {
    // Split the parsedValue around the SUBSCRIPT to get the main value and the subscript value
    const [start, subscript, end] = parsedValue.split(SUBSCRIPT);
    const title = `${startSymbol}${formatToRawNumber(
      21,
      true,
      value
    )}${endSymbol}`;

    // If the component should return a string or if no textProps are provided, use React.Fragment.
    if (string || !textProps) {
      return (
        <>
          {startSymbol}
          {start}
          <sub className='subscript' title={title}>
            {subscript}
          </sub>
          {end}
          {endSymbol}
        </>
      );
    }

    // If textProps are provided, wrap the displayed value with the Text component.
    return (
      <Text {...textProps}>
        {startSymbol}
        {start}
        <sub className='subscript' title={title}>
          {subscript}
        </sub>
        {end}
        {endSymbol}
      </Text>
    );
  }

  // Check if the parsedValue contains a BOLD. If so, we'll display it differently.
  if (parsedValue.includes(BOLD)) {
    // Split the parsedValue around the BOLD to get the main value and the bold value
    const [start, bold] = parsedValue.split(BOLD);

    // Add white space after BOLD element if needed
    const endString = endSymbol.startsWith(' ') ? (
      <>&nbsp;{endSymbol.substring(1)}</>
    ) : (
      endSymbol
    );

    // If the component should return a string or if no textProps are provided, use React.Fragment.
    if (string || !textProps) {
      return (
        <>
          {startSymbol}
          {start}
          <strong>{bold}</strong>
          {endString}
        </>
      );
    }

    // If textProps are provided, wrap the displayed value with the Text component.
    return (
      <Text {...textProps}>
        {startSymbol}
        {start}
        <strong>{bold}</strong>
        {endString}
      </Text>
    );
  }

  // If the parsedValue does not contain a SUBSCRIPT and the component should return a string
  // or if no textProps are provided, use React.Fragment.
  if (string || !textProps) {
    return (
      <>
        {startSymbol}
        {parsedValue || zeroValueDisplay}
        {endSymbol}
      </>
    );
  }

  // If textProps are provided and the parsedValue does not contain a SUBSCRIPT,
  // wrap the displayed value with the Text component.
  return (
    <Text {...textProps}>
      {startSymbol}
      {parsedValue || zeroValueDisplay}
      {endSymbol}
    </Text>
  );
};

export default NumberDisplay;
